/**
 * McpConnectSection (PRP-MCP-1, moved to /assistant by PRP-MCP-10): connects
 * Claude, ChatGPT or Gemini to AIEA over MCP.
 *
 * Shows the consent box, access (trial / plan / allowance), the server URL,
 * how to add it in each chat app, the connected apps, and, collapsed under
 * "For developers", personal access tokens (McpTokensPanel). When `/mcp/status`
 * returns 404 the server has MCP turned off, and when it says `allowed: false`
 * the user is outside the emergency allow list; either way the section renders
 * nothing.
 * PRP-MCP-8 (F1): every account may connect. A free account sees the whole
 * section plus one line: it holds a few chat estimates, and export needs a
 * subscription or the $7 trial (links to both). An older server that still
 * answers `access: 'none'` gets the same treatment.
 *
 * The page's own section frame is passed in as `frame` so the section matches
 * its neighbors and still disappears entirely when MCP is off.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { McpAPI } from '../../lib/mcpApi';
import type { McpConnection, McpStatus } from '../../lib/mcpApi';
import type { ApiError } from '../../types';
import { MCP_USAGE_NOTICE } from '../lib/notices';
import { Button } from './ui/button';
import { Pill } from './ui/pill';
import { Banner } from './ui/banner';
import AssistantConsentPanel from './AssistantConsentPanel';
import CopyField from './McpCopyField';
import McpTokensPanel from './McpTokensPanel';

/** Props the page's Section helper accepts. */
export interface McpSectionFrameProps {
  title: string;
  sub?: string;
  children: ReactNode;
}

/** Props for {@link McpConnectSection}. */
export interface McpConnectSectionProps {
  /** Wrapper that draws the section card. */
  frame: ComponentType<McpSectionFrameProps>;
}

/** Where a user without export goes to subscribe (V2PricingPage). */
export const SUBSCRIBE_PATH = '/pricing';
/** The $7 chat trial on the pricing page (matches app/mcp/data/access.json trial_path). */
export const TRIAL_PATH = '/pricing?trial=1#chat-trial';

/** One line per chat app: where the server URL goes. */
const APP_STEPS: { app: string; steps: string }[] = [
  {
    app: 'Claude',
    steps:
      'Open Settings, then Connectors, and add a custom connector. Paste the server URL above, then sign in to AIEA when Claude asks.',
  },
  {
    app: 'Gemini',
    steps:
      'Use a Gemini app that supports remote MCP connectors: add the server URL above as a custom connector, then sign in to AIEA.',
  },
];

/** Personal-account ChatGPT steps; the server address (step 5) is a CopyField, rendered by the caller. */
const CHATGPT_STEPS_BEFORE_URL = [
  'In ChatGPT, click your account name at the lower left and choose Plugins.',
  'On the Plugins page, click Browse.',
  'At the top right, click the Add dropdown and choose Add a custom MCP server.',
  'Name it AIEA MCP, or any name that means something to you.',
];
const CHATGPT_STEPS_AFTER_URL = [
  'Check the I understand box and click Create as plugin.',
  'ChatGPT sends you to AIEA to sign in. Use your normal AIEA login. After you sign in, you are connected.',
];

/** ChatGPT connect steps: personal accounts (free or paid) by default, workspace admins second. */
function ChatGptSteps({ serverUrl }: { serverUrl: string }) {
  return (
    <div className="space-y-2" data-testid="mcp-chatgpt-steps">
      <h3 className="m-0 text-[12.5px] font-[550] leading-snug text-[var(--tx-hi)]">
        Connect ChatGPT (works on free and paid personal accounts)
      </h3>
      <ol className="m-0 list-decimal space-y-1.5 pl-5 text-[12.5px] text-[var(--tx-mid)]">
        {CHATGPT_STEPS_BEFORE_URL.map((t) => (
          <li key={t}>{t}</li>
        ))}
        <li>
          Under Connection, enter this server address:
          <div className="mt-1.5 max-w-full">
            <CopyField label="ChatGPT server address" value={serverUrl} />
          </div>
        </li>
        {CHATGPT_STEPS_AFTER_URL.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ol>
      <p className="m-0 text-[12.5px] text-[var(--tx-mid)]">
        To check your connection status or disconnect, use the connection status on this page.
      </p>
      <details className="rounded-[var(--r-md)] border border-[var(--bd)] p-3" data-testid="mcp-chatgpt-workspace">
        <summary className="cursor-pointer text-[12.5px] font-[550] text-[var(--tx-hi)]">
          ChatGPT Business or Enterprise workspace
        </summary>
        <p className="mb-0 mt-2 text-[12.5px] text-[var(--tx-mid)]">
          An admin opens the Admin Console, then Plugins, then + Add, and enters the server address above.
          Then each person signs in to AIEA.
        </p>
      </details>
    </div>
  );
}

/** Plain-language line describing the user's access. */
function AccessLine({ status }: { status: McpStatus }) {
  const used = status.used_this_month;
  const period = status.allowance_period === 'lifetime' ? '' : ' this month';
  const allowanceText =
    status.allowance == null
      ? `${used} used${period}, unlimited`
      : `${used} of ${status.allowance} used${period}, ${status.remaining ?? 0} remaining`;
  let pill: ReactNode;
  if (status.access === 'subscription') {
    pill = <Pill kind="ok">{status.plan ? `Plan: ${status.plan}` : 'Subscribed'}</Pill>;
  } else if (status.access === 'role') {
    pill = <Pill kind="ok">Included with your account</Pill>;
  } else if (status.access === 'grant') {
    pill = <Pill kind="ok">Turned on for your account</Pill>;
  } else if (status.access === 'trial') {
    const days = status.trial_days_left ?? 0;
    pill = <Pill kind="info">{`Trial: ${days} ${days === 1 ? 'day' : 'days'} left`}</Pill>;
  } else {
    pill = <Pill kind="neutral">Free</Pill>;
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-[var(--tx-mid)]">
      {pill}
      <span data-testid="mcp-allowance">{allowanceText}</span>
    </div>
  );
}

/** The connect section of the /assistant page. */
export default function McpConnectSection({ frame: Frame }: McpConnectSectionProps) {
  const [status, setStatus] = useState<McpStatus | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [connections, setConnections] = useState<McpConnection[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConnections = useCallback(async () => {
    const res = await McpAPI.listConnections();
    setConnections(res.connections ?? []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await McpAPI.getStatus();
        if (cancelled) return;
        // Limited release (MCP_ALLOWED_USERS): not on the list, so no section at all.
        if (s.allowed === false) {
          setDisabled(true);
          return;
        }
        setStatus(s);
        await loadConnections();
      } catch (err) {
        if (cancelled) return;
        if ((err as ApiError).status === 404) setDisabled(true);
        else setError((err as ApiError).message || 'Could not load your connection settings.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadConnections]);

  if (disabled) return null;

  const isFree = status?.access === 'free' || status?.access === 'none';

  const disconnect = async (clientId: string) => {
    setBusy(true);
    setError(null);
    try {
      await McpAPI.revokeConnection(clientId);
      await loadConnections();
    } catch (err) {
      setError((err as ApiError).message || 'Could not disconnect that app.');
    } finally {
      setBusy(false);
    }
  };

  const serverUrl = status?.server_url ?? '';

  return (
    <Frame
      title="Connect Claude, ChatGPT or Gemini"
      sub="Let a chat app read and edit your estimates. Its edits apply at once and can be undone."
    >
      <div className="space-y-5" data-testid="mcp-connect-section">
        {error && <Banner tone="warn">{error}</Banner>}
        {status && <AssistantConsentPanel />}
        {status ? (
          <>
            <AccessLine status={status} />
            {isFree && (
              <p className="m-0 text-[12.5px] text-[var(--tx-mid)]" data-testid="mcp-free-line">
                {`Your free account holds ${status.allowance ?? 3} chat estimates. Exporting them needs a subscription or the $7, 7-day trial. `}
                <Link to={SUBSCRIBE_PATH} className="text-[var(--accent)] underline">
                  Subscribe
                </Link>
                {' or '}
                <Link to={TRIAL_PATH} className="text-[var(--accent)] underline">
                  start the trial
                </Link>
              </p>
            )}
            <div>
              <div className="mb-1.5 text-[12px] font-[550] text-[var(--tx-mid)]">Server URL</div>
              <CopyField label="server URL" value={serverUrl} />
            </div>
            <ol
              className="m-0 list-none space-y-2 p-0 text-[12.5px] text-[var(--tx-mid)]"
              aria-label="Connect instructions per chat app"
              data-testid="mcp-app-steps"
            >
              {APP_STEPS.map((a) => (
                <li key={a.app}>
                  <b className="font-[550] text-[var(--tx-hi)]">{a.app}.</b> {a.steps}
                </li>
              ))}
            </ol>
            <ChatGptSteps serverUrl={serverUrl} />
          </>
        ) : (
          !error && <div className="text-[12.5px] text-[var(--tx-lo)]">Loading...</div>
        )}
        <p className="m-0 text-[12px] text-[var(--tx-lo)]" data-testid="mcp-usage-notice">
          {MCP_USAGE_NOTICE}
        </p>

        <div>
          <div className="mb-1.5 text-[12px] font-[550] text-[var(--tx-mid)]">Connection status</div>
          {connections.length > 0 ? (
            <ul className="m-0 p-0 list-none divide-y divide-[var(--bd-subtle)]" aria-label="Connected chat apps">
              {connections.map((c) => (
                <li key={c.client_id} className="flex items-center gap-3 py-2">
                  <div className="flex-1 min-w-0 text-[12.5px] text-[var(--tx-hi)]">
                    {c.name}{' '}
                    <span className="text-[var(--tx-lo)]">{c.redirect_host}</span>
                  </div>
                  <Button
                    size="sm"
                    disabled={busy}
                    aria-label={`Disconnect ${c.name}`}
                    onClick={() => void disconnect(c.client_id)}
                  >
                    Disconnect
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            status && (
              <p className="m-0 text-[12.5px] text-[var(--tx-lo)]" data-testid="mcp-no-connections">
                No chat app is connected yet.
              </p>
            )
          )}
        </div>

        <p className="m-0 text-[12.5px] text-[var(--tx-mid)]" data-testid="mcp-edits-notice">
          Edits your chat app makes apply to the estimate at once. They show on the estimate
          page tagged &quot;from chat,&quot; and you can undo any of them there or in the chat.
          Before you export, AIEA asks you to review them once.
        </p>

        {status && (
          <details className="rounded-[var(--r-md)] border border-[var(--bd)] p-3" data-testid="mcp-developers">
            <summary className="cursor-pointer text-[12.5px] font-[550] text-[var(--tx-hi)]">
              For developers: access tokens
            </summary>
            <p className="mb-3 mt-2 text-[12.5px] text-[var(--tx-mid)]">
              Most people never need a token: your chat app signs in for you.
            </p>
            <McpTokensPanel serverUrl={serverUrl} />
          </details>
        )}
      </div>
    </Frame>
  );
}
